import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users,
  UserCheck,
  UserX,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Settings,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const { user, hasModuleAccess } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const statsRes = await axios.get(`${API_URL}/admin/stats`);
      setStats(statsRes.data);

      let status = null;
      if (activeTab === 'pending') status = 'PENDING';
      if (activeTab === 'approved') status = 'APPROVED';
      if (activeTab === 'disapproved') status = 'DISAPPROVED';

      const usersRes = await axios.get(`${API_URL}/admin/users${status ? `?status=${status}` : ''}`);
      setUsers(usersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (userId, newStatus) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/status`, { status: newStatus });
      toast.success('User status updated successfully', {
        position: 'top-right',
        autoClose: 2000
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/admin`, { is_admin: !isAdmin });
      toast.success('Admin status updated successfully', {
        position: 'top-right',
        autoClose: 2000
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      toast.success('User deleted successfully', {
        position: 'top-right',
        autoClose: 2000
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openPermissionsModal = (userObj) => {
    setSelectedUser(userObj);
    setShowPermissionsModal(true);
  };

  const handlePermissionChange = (module, action, value) => {
    setSelectedUser(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...(prev.permissions[module] || {}),
          [action]: value
        }
      }
    }));
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`${API_URL}/admin/users/${selectedUser.id}/permissions`, {
        permissions: selectedUser.permissions
      });
      toast.success('Permissions updated successfully', {
        position: 'top-right',
        autoClose: 2000
      });
      setShowPermissionsModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  // Check admin access
  if (!user?.is_admin && !hasModuleAccess('admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Card className="max-w-md w-full mx-4 shadow-xl">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access the Admin Dashboard</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
              <p className="text-gray-600 text-lg">Manage users, permissions, and system settings</p>
            </div>
            <div className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">{user?.full_name || user?.username}</span>
              {user?.is_admin && (
                <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Admin</span>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs font-medium uppercase tracking-wider mb-1">Total Users</p>
                  <div className="text-3xl font-bold">{stats?.total_users || 0}</div>
                </div>
                <Users className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-xs font-medium uppercase tracking-wider mb-1">Pending</p>
                  <div className="text-3xl font-bold">{stats?.pending_users || 0}</div>
                </div>
                <Clock className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs font-medium uppercase tracking-wider mb-1">Approved</p>
                  <div className="text-3xl font-bold">{stats?.approved_users || 0}</div>
                </div>
                <CheckCircle className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-xs font-medium uppercase tracking-wider mb-1">Disapproved</p>
                  <div className="text-3xl font-bold">{stats?.disapproved_users || 0}</div>
                </div>
                <XCircle className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs font-medium uppercase tracking-wider mb-1">Admins</p>
                  <div className="text-3xl font-bold">{stats?.admin_users || 0}</div>
                </div>
                <Shield className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm border border-gray-200 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              All Users
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              Pending
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Approved
            </TabsTrigger>
            <TabsTrigger value="disapproved" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              Disapproved
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            <Card className="shadow-lg border-0">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <CardTitle className="text-xl">User Management</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Username
                        </th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Full Name
                        </th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Admin
                        </th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="text-lg font-medium">No users found</p>
                            <p className="text-sm">Users will appear here once added to the system</p>
                          </td>
                        </tr>
                      ) : (
                        users.map((userObj) => (
                          <tr key={userObj.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <span className="font-medium text-gray-900">{userObj.username}</span>
                            </td>
                            <td className="px-6 py-4 text-gray-700">{userObj.full_name}</td>
                            <td className="px-6 py-4 text-gray-600 text-sm">{userObj.email}</td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                                {userObj.role}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                userObj.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                                userObj.status === 'PENDING' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {userObj.status === 'APPROVED' && <CheckCircle className="w-3 h-3 mr-1" />}
                                {userObj.status === 'PENDING' && <Clock className="w-3 h-3 mr-1" />}
                                {userObj.status === 'DISAPPROVED' && <XCircle className="w-3 h-3 mr-1" />}
                                {userObj.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {userObj.is_admin ? (
                                <Shield className="w-5 h-5 text-purple-600" />
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2 flex-wrap">
                                {userObj.status === 'PENDING' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleStatusUpdate(userObj.id, 'APPROVED')}
                                      className="bg-green-600 hover:bg-green-700"
                                      data-testid={`approve-user-${userObj.id}`}
                                    >
                                      <UserCheck className="w-4 h-4 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleStatusUpdate(userObj.id, 'DISAPPROVED')}
                                      data-testid={`reject-user-${userObj.id}`}
                                    >
                                      <UserX className="w-4 h-4 mr-1" />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {userObj.status === 'APPROVED' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openPermissionsModal(userObj)}
                                      className="border-gray-300 hover:border-blue-500 hover:text-blue-600"
                                      data-testid={`permissions-user-${userObj.id}`}
                                    >
                                      <Settings className="w-4 h-4 mr-1" />
                                      Permissions
                                    </Button>
                                    {userObj.id !== user?.id && (
                                      <Button
                                        size="sm"
                                        variant={userObj.is_admin ? 'destructive' : 'default'}
                                        onClick={() => handleToggleAdmin(userObj.id, userObj.is_admin)}
                                        className={!userObj.is_admin ? 'bg-purple-600 hover:bg-purple-700' : ''}
                                        data-testid={`toggle-admin-${userObj.id}`}
                                      >
                                        <Shield className="w-4 h-4 mr-1" />
                                        {userObj.is_admin ? 'Remove Admin' : 'Make Admin'}
                                      </Button>
                                    )}
                                  </>
                                )}
                                {userObj.id !== user?.id && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDeleteUser(userObj.id)}
                                    data-testid={`delete-user-${userObj.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Permissions Modal */}
        {showPermissionsModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm" data-testid="permissions-modal">
            <Card className="max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl border-0">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">Manage Permissions</CardTitle>
                    <p className="text-blue-100 mt-1">{selectedUser.full_name} ({selectedUser.username})</p>
                  </div>
                  <button
                    onClick={() => setShowPermissionsModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                    data-testid="close-permissions-modal"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {selectedUser.is_admin ? (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mr-4">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-purple-900 font-bold text-lg">Administrator Privileges</p>
                        <p className="text-purple-700 text-sm">This user has full system access</p>
                      </div>
                    </div>
                    <p className="text-purple-600 text-sm">All permissions are automatically granted to administrators</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {['dashboard', 'membership', 'subscription', 'cashCounter', 'pettyCash', 'coins', 'kitty', 'settlement'].map(module => (
                      <div key={module} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <p className="font-semibold text-gray-900 capitalize text-lg">{module}</p>
                          {module === 'dashboard' && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Read Only</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {module === 'dashboard' ? (
                            <label className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-md hover:bg-gray-100 cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                checked={selectedUser.permissions[module]?.view || false}
                                onChange={(e) => handlePermissionChange(module, 'view', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                data-testid={`permission-${module}-view`}
                              />
                              <span className="text-sm font-medium text-gray-700">View</span>
                            </label>
                          ) : (
                            ['view', 'create', 'edit', 'delete'].map(action => (
                              <label key={action} className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-md hover:bg-gray-100 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={selectedUser.permissions[module]?.[action] || false}
                                  onChange={(e) => handlePermissionChange(module, action, e.target.checked)}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                  data-testid={`permission-${module}-${action}`}
                                />
                                <span className="text-sm font-medium text-gray-700 capitalize">{action}</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => setShowPermissionsModal(false)}
                    className="px-6"
                    data-testid="cancel-permissions"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePermissions}
                    className="px-6 bg-blue-600 hover:bg-blue-700"
                    data-testid="save-permissions"
                  >
                    Save Permissions
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
